import type { ComponentType } from 'react';
import {
  MdAnalytics,
  MdArchitecture,
  MdCheck,
  MdChecklist,
  MdClose,
  MdDocumentScanner,
  MdDraw,
  MdGavel,
  MdImage,
  MdLogin,
  MdLogout,
  MdPanTool,
  MdPrecisionManufacturing,
  MdRoute,
  MdStickyNote2,
  MdSwapHoriz,
  MdViewInAr,
} from 'react-icons/md';

/**
 * Semantic-icon-id -> React icon component mapping.
 *
 * The backend's `NodeDefinitionDto.displayMetadata.iconId` carries one of
 * the keys below (defined as constants in `Icons.java`). Those backend
 * strings describe WHAT the icon represents (e.g. `"input"`, `"render-3d"`),
 * not the underlying glyph from a specific icon pack — so swapping
 * `react-icons/md` for any other icon library is a frontend-only change.
 *
 * This is the ONLY place the frontend hardcodes node-related visual
 * primitives — everything else (label, description, group, port shape,
 * config shape) comes from the catalog endpoint. Unknown ids fall back
 * to a generic icon so a backend deploy that ships a new id never hard
 * breaks the editor; the FE catalog can catch up in a follow-up PR.
 */
type IconComponent = ComponentType<{ size?: number | string }>;

const FALLBACK_ICON: IconComponent = MdChecklist;

const ICONS: Record<string, IconComponent> = {
  // Structural nodes
  input: MdLogin,
  output: MdLogout,
  gate: MdGavel,
  note: MdStickyNote2,
  manual: MdPanTool,

  // Branch glyphs (Gate's approved / rejected)
  check: MdCheck,
  close: MdClose,

  // Task agents
  task: MdChecklist,
  'render-3d': MdViewInAr,
  'cad-convert': MdSwapHoriz,
  'cad-parametric': MdPrecisionManufacturing,
  'image-gen': MdImage,
  'mesh-processing': MdViewInAr,
  'cad-nurbs': MdArchitecture,
  'model-3d-gen': MdViewInAr,
  'cam-toolpath': MdRoute,
  'sim-visualization': MdAnalytics,
  'technical-drawing': MdDraw,
  'drawing-interpret': MdDocumentScanner,
};

export function resolveIcon(iconId: string | null | undefined): IconComponent {
  if (!iconId) return FALLBACK_ICON;
  return ICONS[iconId] ?? FALLBACK_ICON;
}

export const ICON_IDS = Object.keys(ICONS);
